import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader } from 'reactstrap';

import { checkProofSupport } from '~libs/sessionKey';
import { publishSessionMessage } from '~libs/sessionSync';
import { leaveForTp1SignIn, recordSignInRoute, takeTp1ReturnPath, usesTp1SignIn } from '~libs/signInRoute';

import { Spinner } from '~components';

import { authenticateHandoff, authenticate as authenticateSession } from '~main/actions/login';
import { bootstrapSession, toSessionTiming } from '~main/actions/session';

import { removeAuthenticated } from '~main/reducers/login';

import { selectAuthenticated } from '~main/selectors/login';

const SessionCheck: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setAuthenticated] = useState(false);
  const sessionAuthenticated = useAppSelector(selectAuthenticated);

  // A handoff ticket can only be redeemed once, so a second attempt would spend a
  // ticket that has already been claimed and fail. Guard against the effect running
  // more than once for a single mount.
  const hasAttempted = useRef(false);
  // The page this tab was on before it left for TP1 to sign in. TP1 always hands back to home, so after a successful
  // redeem this takes its place.
  const returnPath = useRef<string | null>(null);

  useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;
    authenticate();
  }, []);

  useEffect(() => {
    if (isAuthenticated && sessionAuthenticated) {
      navigate(getTarget());
    }
  }, [isAuthenticated, sessionAuthenticated]);

  const getTarget = () => {
    const { state } = location;
    if (!state) return '/';

    const { from } = state;
    if (from && from !== '/') return from;
    return returnPath.current ?? '/';
  };

  const authenticate = async () => {
    const { state } = location;
    if (!state) {
      if (usesTp1SignIn() && leaveForTp1SignIn()) return;
      navigate('/login');
      return;
    }

    // The new session is bound to a key this browser creates during the redeem. Without one there is nothing
    // to bind to, so refuse before spending a single-use ticket.
    if (!(await checkProofSupport())) {
      navigate('/login');
      return;
    }

    const { sessionKey, isHandoff, isSso } = state;

    // The existing session is deliberately left in place until the new one is issued.
    // Clearing it first would mean a failed redeem signs the user out of a session that
    // was working - see the fallback below.
    //
    // A consequence is that the redeem request still carries the previous occupant's tav_sid
    // cookie. That is safe: neither redeem endpoint sits behind the API's auth middleware, and
    // both resolve identity purely from the body - the ticket, or the decrypted legacy value - so
    // the stale cookie cannot decide who the caller ends up signed in as. The request also carries
    // a fresh proof_key (added by the axios interceptor), and on success the API revokes the
    // presented session and binds the new one to that new key.
    const { payload } = isHandoff
      ? await dispatch(authenticateHandoff({ handoff: sessionKey }))
      : await dispatch(authenticateSession({ session: sessionKey }));

    if (payload?.success) {
      recordSignInRoute(isSso ? 'sso' : 'tp1');
      returnPath.current = takeTp1ReturnPath();
      // A tab left waiting on the sign-in prompt (the user signed in here from its organisation option)
      // closes the prompt and carries on with this new window. The new key reaches it as 'key-changed'.
      if (payload.session) {
        const { expiresAt, absoluteExpiresAt, reauthExpiresAt } = toSessionTiming(payload.session);
        publishSessionMessage({ type: 'reauthed', expiresAt, absoluteExpiresAt, reauthExpiresAt });
      }
      setAuthenticated(true);
      return;
    }

    // A handoff ticket is single-use, so re-entering this route with a spent ticket is
    // an ordinary thing for a user to do - pressing Back lands here. When they already
    // have a valid session, keep it and send them on rather than signing them out.
    if (sessionAuthenticated) {
      navigate(getTarget(), { replace: true });
      return;
    }

    // A tab opened fresh from a spent link has not asked the API about its session yet, because the
    // session lives in an httpOnly cookie. Check before signing out, so a valid session is kept here too.
    const bootstrap = await dispatch(bootstrapSession());
    if (bootstrapSession.fulfilled.match(bootstrap)) {
      navigate(getTarget(), { replace: true });
      return;
    }

    dispatch(removeAuthenticated());
    // The handoff failed whatever the marker says, so TP1 is the way back in. The guard stops a loop.
    if (leaveForTp1SignIn()) return;
    navigate('/login');
  };

  return (
    <div className="d-flex justify-content-center mt-5 login">
      <Card className="rounded">
        <CardHeader className="d-flex justify-content-center bg-menu-background">
          <img src="/assets/primary_logo.png" alt="Logo" />
        </CardHeader>
        <CardBody className="d-flex flex-column text-center">
          <Spinner loading={true} type="beat" className="mt-4" />
          <h2 className="mt-4">Authenticating</h2>
          <p className="mt-1 mb-1">You will be automatically navigated in a moment.</p>
          <p className="mt-1">Thank you for your patience</p>
        </CardBody>
      </Card>
    </div>
  );
};

export default SessionCheck;
